/**
 * Project Name: Brainsie eCommerce v4
 * Component: Blog Card
 * Description: Blog post card component with image, title, description and read more link
 * Author: Brainsie Team
 * Date Created: October 30, 2025
 */

import React from "react";
import { Box, Card, CardContent, Typography, Link } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";

export default function BlogCard({
  component,
  section,
  darkMode,
  theme,
  gridIndex,
}) {
  // Get component settings
  const componentSettings = component.Component_Settings
    ? JSON.parse(component.Component_Settings)
    : {};

  // Default settings
  const cardPadding = componentSettings.cardPadding || 24;
  const readMoreLink = componentSettings.readMoreLink || "#";

  // Get image from Media_Files array (first image)
  const getImageFromMediaFiles = () => {
    try {
      let mediaFile;
      if (Array.isArray(component.Media_Files)) {
        mediaFile = component.Media_Files[0];
      } else if (typeof component.Media_Files === "string") {
        const parsed = JSON.parse(component.Media_Files);
        mediaFile = Array.isArray(parsed) ? parsed[0] : parsed;
      } else if (component.Media_Files) {
        mediaFile = component.Media_Files;
      }
      return mediaFile || null;
    } catch (e) {
      console.log("Error parsing Media_Files:", e);
      return null;
    }
  };

  const imageFile = getImageFromMediaFiles();
  const hasImage = imageFile !== null;

  // Debug logging
  console.log("🎯 BLOG CARD DEBUG:", {
    componentName: component.Component_Name,
    primaryText: component.Primary_Text,
    secondaryText: component.Secondary_Text,
    descriptiveText: component.Descriptive_Text,
    mediaFiles: component.Media_Files,
    imageFile: imageFile,
    hasImage: hasImage,
    readMoreLink: readMoreLink,
  });

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 400,
        borderRadius: "12px",
        backgroundColor: darkMode ? theme.palette.grey[900] : "#ffffff",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        border: `1px solid ${darkMode ? theme.palette.grey[700] : "#e0e0e0"}`,
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      {/* Large Image Section - Takes up most of the card */}
      <Box
        sx={{
          width: "100%",
          height: "70%", // Large portion for image like in wireframe
          minHeight: 280,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {hasImage ? (
          <Box
            component="img"
            src={
              imageFile.startsWith("http")
                ? imageFile
                : `${process.env.BASEURL}${imageFile}`
            }
            alt={component.Primary_Text || "Blog post"}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            onError={(e) => {
              console.log("Blog image failed to load:", imageFile);
              e.target.style.display = "none";
            }}
          />
        ) : (
          // Wireframe-style placeholder
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `3px solid ${darkMode ? "#666" : "#ccc"}`,
              borderRadius: "8px",
              margin: "12px",
              height: "calc(100% - 24px)",
            }}
          >
            {/* Wireframe-style mountains and sun placeholder */}
            <Box
              sx={{
                width: "60%",
                height: "60%",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Sun/circle */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  border: `3px solid ${darkMode ? "#666" : "#999"}`,
                  position: "absolute",
                  top: 20,
                  right: 20,
                }}
              />
              {/* Mountains */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "50%",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: "10%",
                    width: 0,
                    height: 0,
                    borderLeft: "30px solid transparent",
                    borderRight: "30px solid transparent",
                    borderBottom: `40px solid ${darkMode ? "#666" : "#999"}`,
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    right: "20%",
                    width: 0,
                    height: 0,
                    borderLeft: "25px solid transparent",
                    borderRight: "25px solid transparent",
                    borderBottom: `30px solid ${darkMode ? "#666" : "#999"}`,
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Compact Content Section - Bottom 30% like wireframe */}
      <Box
        sx={{
          height: "30%",
          minHeight: 120,
          p: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ flex: 1 }}>
          {/* Title (Primary_Text) */}
          {component.Primary_Text && component.Primary_Text !== "NA" && (
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                lineHeight: 1.2,
                mb: 0.5,
                color: darkMode ? theme.palette.common.white : "#000000",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {component.Primary_Text}
            </Typography>
          )}

          {/* Description (Descriptive_Text) - More compact */}
          {component.Descriptive_Text &&
            component.Descriptive_Text !== "NA" && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: 1.4,
                  color: darkMode ? theme.palette.grey[400] : "#666666",
                  mb: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {component.Descriptive_Text}
              </Typography>
            )}
        </Box>

        {/* Read More Link - Bottom right */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            mt: 0.5,
          }}
        >
          <Link
            href={readMoreLink}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: darkMode ? theme.palette.primary.light : "#000000",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                gap: 1,
                textDecoration: "none",
              },
            }}
          >
            read more
            <ArrowForward sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      </Box>
    </Card>
  );
}
